import throttle from "lodash/throttle";
import { uiStore } from "@/store/store";
import { nextTick } from "vue";

function getElementRect(element: HTMLElement | null) {
  // Handle .oxy-component, which uses `display: contents`. We return its first visible child.
  if (
    element?.parentElement?.classList?.contains("oxy-component") &&
    element?.children?.length
  ) {
    return element.children[0]?.getBoundingClientRect();
  }

  return element?.parentElement?.getBoundingClientRect();
}

// Compare two rect objects to see if they're different (with 1px tolerance)
function rectsEqual(
  rect1: DOMRect | null,
  rect2: {
    top: number;
    left: number;
    bottom: number;
    right: number;
    width: number;
    height: number;
    y: number;
    x: number;
  } | null
): boolean {
  if (!rect1 || !rect2) return rect1 === rect2;

  return (
    Math.abs(rect1.top - rect2.top) < 1 &&
    Math.abs(rect1.left - rect2.left) < 1 &&
    Math.abs(rect1.bottom - rect2.bottom) < 1 &&
    Math.abs(rect1.right - rect2.right) < 1 &&
    Math.abs(rect1.width - rect2.width) < 1 &&
    Math.abs(rect1.height - rect2.height) < 1
  );
}

export function calcBackdropCutoutSize(element: HTMLElement) {
  nextTick(() => {
    const rect = getElementRect(element);

    if (!rect || !uiStore.inlineEditingContext) return;

    const currentRect = uiStore.inlineEditingRect;

    if (currentRect && rectsEqual(rect, currentRect)) {
      return;
    }

    uiStore.setInlineEditingRect(rect);
  });
}

export function startBackdropListeners(element: HTMLElement) {
  const calcFn = () => calcBackdropCutoutSize(element);

  // Throttle scroll events to 16ms (60fps) to prevent excessive calculations
  const throttledCalcFn = throttle(calcFn, 16);
  const doc = window.Breakdance?.canvas?.document;

  // Listen for scroll events
  doc.addEventListener("scroll", throttledCalcFn, { passive: true });

  // Listen for resize events
  const resizeObserver = new ResizeObserver(calcFn);

  const ResizeObserverIframe = doc.defaultView?.ResizeObserver;
  const resizeObserverIframe = ResizeObserverIframe
    ? new ResizeObserverIframe(calcFn)
    : null;

  resizeObserver?.observe(document.querySelector("#iframe-wrapper") as Element);
  resizeObserverIframe?.observe(element.parentElement ?? element);

  // Calculate the initial backdrop cutout size
  calcFn();

  // Unsubscribe from events
  return () => {
    doc.removeEventListener("scroll", throttledCalcFn);
    resizeObserver?.disconnect();
    resizeObserverIframe?.disconnect();
  };
}
